<template>
  <div>
    <div class="buttons-container">
      <router-link :to="{ name: 'MonitoringEstablishments' }">
        <Button title="Tableaux" type="primary" />
      </router-link>
      <router-link :to="{ name: 'MonitoringEstablishmentsCharts' }">
        <Button title="Graphs" type="primary" :active="true" />
      </router-link>
    </div>

    <div class="charts-container">
      <div class="establishments-list">

        <p class="title">Vue</p>

        <div class="buttons-container-view">
          <Button
            title="3 derniers mois"
            type="primary"
            size="small"
            :active="viewRange === 'last3months'"
            @click="viewRange = 'last3months'"
          />
          <Button
            title="Depuis le début"
            type="primary"
            size="small"
            :active="viewRange === 'fromStart'"
            @click="viewRange = 'fromStart'"
          />
        </div>

        <div class="establishment-search">
          <Input
            title="Rechercher"
            name="search"
            v-model="search"
          />
        </div>

        <div class="buttons-container">
          <Button
            title="Tous"
            type="primary"
            size="small"
            :active="selectedEstablishments.length === filteredEstablishments.length"
            @click="selectAllEstablishments"
          />
          <Button
            title="Aucun"
            type="primary"
            size="small"
            :active="selectedEstablishments.length === 0"
            @click="unSelectAllEstablishments"
          />
        </div>

        <p class="title">Établissements</p>

        <div v-for="establishment in filteredEstablishments" :key="establishment.establishmentId"
          class="establishment"
          :class="{ selected: selectedEstablishmentsIds.includes(establishment.establishmentId) }"
          :data-color="establishment.color"
          @click="toggleEstablishment(establishment)"
        >
          <div class="dot" :style="{ background: selectedEstablishmentsIds.includes(establishment.establishmentId) ? establishment.color : '#8E8E95' }"></div>
          {{ establishment.name }}

          <transition name="fade">
            <div class="loading" v-if="selectedEstablishmentsIds.includes(establishment.establishmentId) && !establishment.computedStats.usageDaysLast90days">
              <SVGSpinner />
            </div>
          </transition>
        </div>
      </div>

      <transition name="fade" mode="out-in">
        <div class="charts" v-if="selectedEstablishments.length > 0" key="charts">
          <div class="chart">
            <EstablishmentsUsageDaysChart
              :patients="selectedEstablishments"
            />
          </div>

          <div class="chart">
            <EstablishmentsUsageChart
              :patients="selectedEstablishments"
            />
          </div>

          <div class="chart">
            <EstablishmentsPerformanceChart
              :patients="selectedEstablishments"
            />
          </div>
        </div>
        <div class="charts empty" v-else key="charts-empty">
          <p>Sélectionner un ou plusieurs établissements pour visualiser leurs statistiques</p>
        </div>
      </transition>
    </div>
  </div>
</template>

<script>
import _ from 'lodash'
import { mapGetters } from 'vuex'
import Button from '@/components/Button'
import Input from '@/components/Forms/Fields/Input'
import EstablishmentsUsageDaysChart from '@/components/Charts/PatientsUsageDaysChart'
import EstablishmentsUsageChart from '@/components/Charts/PatientsUsageChart'
import EstablishmentsPerformanceChart from '@/components/Charts/PatientsPerformanceChart'
import SVGSpinner from '@/assets/icons/spinner-tail.svg'

export default {
  name: 'MonitoringEstablishmentsCharts',
  components: {
    Button, Input, EstablishmentsUsageDaysChart, EstablishmentsUsageChart, EstablishmentsPerformanceChart, SVGSpinner
  },
  data () {
    return {
      viewRange: 'last3months',
      search: ''
    }
  },
  async created () {
    await this.$store.dispatch('admin/getEstablishments')
  },
  computed: {
    ...mapGetters({
      establishmentsList: 'admin/establishments',
      selectedEstablishmentsIds: 'admin/selectedEstablishments'
    }),
    sortedEstablishments () {
      return _.sortBy(this.establishmentsList, ['name'])
    },
    filteredEstablishments () {
      return this.sortedEstablishments.filter(establishment => establishment.name.toLowerCase().includes(this.search.toLowerCase()))
    },
    selectedEstablishments () {
      return this.sortedEstablishments.filter(establishment => this.selectedEstablishmentsIds.includes(establishment.establishmentId))
    }
  },
  methods: {
    toggleEstablishment (establishment) {
      if(this.selectedEstablishmentsIds.includes(establishment.establishmentId)) {
        this.$store.dispatch('admin/unselectEstablishment', establishment.establishmentId)
      } else {
        this.$store.dispatch('admin/selectEstablishment', establishment.establishmentId)
        this.$store.dispatch('admin/getEstablishmentStats', { establishmentId: establishment.establishmentId, days: 91 })
      }
    },
    selectAllEstablishments () {
      this.sortedEstablishments.forEach(establishment => {
        this.$store.dispatch('admin/selectEstablishment', establishment.establishmentId)
        this.$store.dispatch('admin/getEstablishmentStats', { establishmentId: establishment.establishmentId, days: 91 })
      })
    },
    unSelectAllEstablishments () {
      this.sortedEstablishments.forEach(establishment => {
        this.$store.dispatch('admin/unselectEstablishment', establishment.establishmentId)
      })
    }
  }
}
</script>


<style lang="scss" scoped>
.buttons-container{
  margin-top: 24px;
  display: flex;

  a{
    margin: 0 6px;

    &:first-child{
      margin-left: 0;
    }
    &:last-child{
      margin-right: 0;
    }
  }
}

.charts-container{
  display: flex;
  align-items: flex-start;
  margin-top: 24px;

  .establishments-list{
    position: sticky;
    top: 0;
    width: 15%;
    border-radius: 10px;
    border: 1px solid #F0F0F0;
    padding: 0 0 15px 0;

    .buttons-container-view{
      margin-top: 0;
      flex-direction: column;

      .button{
        margin-bottom: 10px;
      }
    }

    .buttons-container{
      margin-top: 0;
      justify-content: center;
    }

    .title{
      font-size: 1.2rem;
      font-weight: 420;
      margin: 12px 0;
      padding: 0 10px;
    }

    .establishment{
      padding: 5px 10px;
      cursor: pointer;
      white-space: nowrap;
      transition: all 0.25s;
      position: relative;

      &:hover{
        color: $main-color;
      }

      .dot{
        width: 10px;
        height: 10px;
        border-radius: 50%;
        margin-right: 12px;
        background: $light-text-color;
        display: inline-block;
      }

      .loading{
        width: 20px;
        height: 20px;
        position: absolute;
        left: 5px;
        top: 3px;

        svg{
          width: 100%;

          path{
            stroke: $main-color;
          }
        }
      }

      &.selected{
        font-weight: 420;
      }
    }
  }

  .charts{
    overflow: scroll;
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    grid-gap: 24px;
    border-radius: 10px;
    border: 1px solid #F0F0F0;
    width: 85%;
    padding: 10px;
    margin-left: 24px;

    &.empty{
      display: flex;
      align-items: center;
      justify-content: center;
      align-self: stretch;

      p{
        font-size: 1.2rem;
        font-weight: 420;
      }
    }
  }
}
</style>
